/**
 * @override
 * Enhanced ImageGallery with primary images/video cycler and Cylindo 3D viewer
 * - Primary images and videos displayed as Swiper carousel
 * - Videos: YouTube, Vimeo, or direct URL — muted autoplay with overlay controls
 * - Cylindo 3D viewer for complex products (when available)
 * - AR link for products with AR capability
 */

import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';

import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image';
import {
  Box, HStack, IconButton, Skeleton as ChakraSkeleton, Text, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils';


import {Swiper} from '@amblique/king-living/app/components/shared/swiper';
import CylindoARLink from '@amblique/king-living/app/components/cylindo-ar-link';
import CylindoViewer from '@amblique/king-living/app/components/cylindo-viewer';
import GalleryVideoSlide from '@amblique/king-living/app/components/gallery-video-slide';
import ImageNotAvailable from '@amblique/king-living/app/components/image-not-available';
import {
  ArIcon, CloseIcon, CylindoZoomIcon, CylindoZoomOutIcon, DragToRotateIcon, FullscreenIcon,
} from '@amblique/king-living/app/components/icons';
import {useCylindoAvailability} from '@amblique/king-living/app/hooks/use-cylindo-availability';
import {useVisualViewportRect} from '@amblique/king-living/app/hooks/use-visual-viewport-rect';
import {
  buildGalleryMediaKey,
  updateSwiperPreservingSlide,
} from '@amblique/king-living/app/utils/gallery-swiper-utils';
import {mergeProductWithVariantCylindo} from '@amblique/king-living/app/utils/cylindo-params';

const LARGE = 'large';

/** theme breakpoints.xl — desktop gallery min-height cap (mobile uses uncapped measure). */
const DESKTOP_MIN_WIDTH_PX = 1280;
const UNIFIED_SLIDE_MIN_H_MAX_DESKTOP_PX = 600;
const CYLINDO_ZOOMED_IN_THRESHOLD = 1.05; // Drives zoom-out enablement and drag-to-rotate visibility above zoom > 1.

function withCylindoViewer(wrapperRef, fn) {
  const viewer = wrapperRef?.current?.querySelector('cylindo-viewer');

  if (viewer) fn(viewer);
}

/**
 * The skeleton representation of the image gallery component. Use this component while
 * you are waiting for product data to be returnd from the server.
 */
export const Skeleton = ({size}) => {
  const styles = useMultiStyleConfig('ProductImageGallery', {size});

  return (
    <Box data-testid="sf-image-gallery-skeleton">
      <ChakraSkeleton aspectRatio={25 / 12} {...styles.heroImageSkeleton} />
    </Box>
  );
};

Skeleton.propTypes = {
  size: PropTypes.string,
};

const ImageGallery = ({
  forceCylindoCheck = false,
  hideOverlayButtons = false,
  isSimpleProduct = false,
  lazy = false,
  product,
  selectedVariationAttributes = {},
  size,
}) => {
  // ─── Refs ──────────────────────────────────────────────────────────────────
  const cylindoWrapperRef = useRef(null);
  const heroSwiperRef = useRef(null);
  const swiperRef = useRef(null);
  /** Prior `realIndex` for loop-wrap mute reset. */
  const prevRealIndexForLoopMuteRef = useRef(0);
  const heroImagesForSwiperRef = useRef(undefined);
  /** Skip mobile remeasure on scroll-driven ResizeObserver (iOS address bar). */
  const skipMobileRemeasureRef = useRef(false);
  // Tracks the last galleryProduct used to render content. Compared in render (before any
  // async effect runs) to detect a product switch and show skeleton immediately.
  const galleryProductRef = useRef(null);

  // ─── State ─────────────────────────────────────────────────────────────────
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [cylindoError, setCylindoError] = useState(false);
  const [isCylindoFullscreen, setIsCylindoFullscreen] = useState(false);
  const [isCylindoZoomedIn, setIsCylindoZoomedIn] = useState(false);
  const [videoMutedMap, setVideoMutedMap] = useState({});
  /** All slides use minHeight = tallest slide (px), set via --hero-gallery-slide-min-h */
  const [unifiedSlideMinPx, setUnifiedSlideMinPx] = useState(null);

  // ─── Memos ─────────────────────────────────────────────────────────────────
  // useVariationParams (retail) builds a new object every parent render via reduce().
  // Do not use that object as a useMemo dependency — it forces merge + JSON.parse on every
  // scroll-driven re-render (AddToCartStrip, header, etc.) and trips isProductSwitching /
  // useCylindoAvailability so the whole gallery flashes.
  const variationAttributesKey = useMemo(() => {
    const s = selectedVariationAttributes;

    if (!s || typeof s !== 'object') return '';

    return Object.keys(s)
      .filter((k) => s[k] != null && s[k] !== '')
      .sort()
      .map((k) => `${k}:${String(s[k])}`)
      .join('|');
  }, [selectedVariationAttributes]);

  const galleryProduct = useMemo(
    () => {
      if (!product) return null;

      return mergeProductWithVariantCylindo(product, selectedVariationAttributes);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedVariationAttributes is new object every render from useVariationParams; variationAttributesKey is the stable signal.
    [product, variationAttributesKey]
  );

  // isProductSwitching is true on the render immediately after a product/variant change,
  // before useCylindoAvailability's async effect has fired. This prevents one frame of stale
  // content (isLoading=false with old state) that causes the "flash of primary image" issue.
  // galleryProductRef.current is null until the first useLayoutEffect fires, so the initial
  // render (null !== null = false) correctly skips the skeleton.
  const isProductSwitching = galleryProductRef.current !== null &&
    galleryProduct !== galleryProductRef.current;

  const styles = useMultiStyleConfig('ImageGallery', {size});

  // Parse c_processedImageGroups from galleryProduct (not raw product) so variant-specific
  // PIM groups injected by mergeProductWithVariantCylindo are included.
  // masterImageSource: baseline source from the first large group without variationAttributes.
  const {imageGroups, masterImageSource} = useMemo(() => {
    if (!galleryProduct?.c_processedImageGroups) {
      return {imageGroups: [], masterImageSource: 'cylindo'};
    }

    try {
      const processedData = JSON.parse(galleryProduct.c_processedImageGroups);

      if (processedData?.imageGroups && Array.isArray(processedData.imageGroups)) {
        const masterLargeGroup = processedData.imageGroups.find(
          (g) => g.viewType === 'large' && !g.variationAttributes
        );

        return {
          imageGroups: processedData.imageGroups,
          masterImageSource: masterLargeGroup?.imageSource || 'cylindo',
        };
      }
    } catch (error) {
      console.error('[ImageGallery] Failed to parse processed image groups:', error);
    }

    return {imageGroups: [], masterImageSource: 'cylindo'};
  }, [galleryProduct]);

  // Resolve the hero image group for the current variation.
  // Only call findImageGroupBy when a variant is actually selected — when called with
  // an empty object its [].every(...) is vacuously true and matches the first group
  // regardless of variationAttributes, which would pick a stale variant group.
  // Priority: variant-specific match → master group (no variationAttributes).
  const heroImageGroup = useMemo(() => {
    const sel = selectedVariationAttributes || {};
    const hasSelection = Object.keys(sel).some(
      (k) => sel[k] != null && sel[k] !== ''
    );

    if (hasSelection) {
      const variantGroup = findImageGroupBy(imageGroups, {
        viewType: LARGE,
        selectedVariationAttributes: sel,
      });

      if (variantGroup) return variantGroup;
    }

    return imageGroups?.find((g) => g.viewType === LARGE && !g.variationAttributes) ?? null;
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedVariationAttributes identity churns each render; variationAttributesKey is stable.
  [imageGroups, variationAttributesKey]);

  heroImagesForSwiperRef.current = heroImageGroup?.images;

  const heroMediaKey = useMemo(
    () => buildGalleryMediaKey(heroImageGroup?.images),
    [heroImageGroup?.images]
  );

  const imageSource = heroImageGroup?.imageSource ?? masterImageSource;

  // Priority: Video (within PIM) > PIM images > Cylindo 3D.
  // Cylindo is only attempted when there are no PIM images to display.
  const hasPimImages = heroImageGroup?.imageSource === 'pim' && heroImageGroup?.images?.length > 0;
  const hasCylindoAttrs = Boolean(galleryProduct?.c_cylindoId || galleryProduct?.cylindoId);

  // Show Cylindo 3D when no PIM images exist, the product has Cylindo attributes, and either:
  //   • forceCylindoCheck (customize flyout — skip isSimpleProduct gate for option products)
  //   • or this is a complex product (isSimpleProduct prop = false)
  const shouldShow3D = !hasPimImages && hasCylindoAttrs && (forceCylindoCheck || !isSimpleProduct);

  const widths = useMemo(() => ({
    base: '100vw',
    lg: styles.heroImage.maxWidth[3],
  }), [styles.heroImage.maxWidth[3]]);

  // ─── Custom hooks ──────────────────────────────────────────────────────────
  // Pass 'cylindo' as imageSource when shouldShow3D — we already know the product has Cylindo
  // attrs, bypassing the imageSource guard inside useCylindoAvailability.
  const {has3D, hasAR, cylindoParams, isLoading} = useCylindoAvailability(
    shouldShow3D ? galleryProduct : null,
    imageGroups,
    shouldShow3D ? 'cylindo' : imageSource,
    heroImageGroup
  );

  const viewportRect = useVisualViewportRect(isCylindoFullscreen);

  // ─── Callbacks ────────────────────────────────────────────────────────────
  /**
   * `activeSlideIndex` follows `realIndex` (0..n-1 in the media array) so it matches `map` indices
   * and video `isActive` / `tabIndex` when Swiper uses loop DOM clones. `activeIndex` is not usable for that.
   * Same `realIndex` transition detects loop wrap for mute reset.
   */
  const syncActiveSlideFromSwiper = useCallback((swiper) => {
    const nextReal =
      typeof swiper?.realIndex === 'number'
        ? swiper.realIndex
        : typeof swiper?.activeIndex === 'number'
          ? swiper.activeIndex
          : 0;
    setActiveSlideIndex(nextReal);

    const images = heroImagesForSwiperRef.current;
    const n = images?.length ?? 0;
    const prevReal = prevRealIndexForLoopMuteRef.current;

    if (n > 1 && images) {
      const loopedAcrossEnds =
        (prevReal === n - 1 && nextReal === 0) || (prevReal === 0 && nextReal === n - 1);
      if (loopedAcrossEnds) {
        setVideoMutedMap((old) => {
          const m = {...old};
          images.forEach((med, idx) => {
            if (med?.mediaType === 'video') {
              m[idx] = true;
            }
          });
          return m;
        });
      }
    }
    prevRealIndexForLoopMuteRef.current = nextReal;
  }, []);

  const measureUnifiedSlideHeight = useCallback(({force = false} = {}) => {
    const root = heroSwiperRef.current;
    if (!root || typeof window === 'undefined') return;

    const isDesktop = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH_PX}px)`).matches;

    if (!isDesktop && skipMobileRemeasureRef.current && !force) {
      return;
    }

    const slides = root.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)');
    if (!slides.length) return;

    let maxH = 0;

    slides.forEach((slide) => {
      const w = slide.getBoundingClientRect().width;
      if (!w) return;

      slide.querySelectorAll('img').forEach((img) => {
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          const h = (img.naturalHeight / img.naturalWidth) * w;
          maxH = Math.max(maxH, h);
        }
      });

      const ambVideo = slide.querySelector('.amb-video');
      if (ambVideo) {
        const vh = ambVideo.getBoundingClientRect().height || ambVideo.offsetHeight;
        maxH = Math.max(maxH, vh || (w * 9) / 16);
      }

      maxH = Math.max(maxH, slide.offsetHeight, slide.scrollHeight);
    });

    const slideW = slides[0].getBoundingClientRect().width;
    const designFloor = slideW > 0 ? (slideW * 12) / 25 : 0;
    const unified = Math.max(maxH, designFloor);

    if (Number.isFinite(unified) && unified > 0) {
      const rounded = Math.ceil(unified);
      setUnifiedSlideMinPx(
        isDesktop ? Math.min(rounded, UNIFIED_SLIDE_MIN_H_MAX_DESKTOP_PX) : rounded
      );

      if (!isDesktop) {
        skipMobileRemeasureRef.current = true;
      }
    }
  }, []);

  const scheduleMeasureUnifiedHeight = useCallback(({force = false} = {}) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => measureUnifiedSlideHeight({force}));
    });
  }, [measureUnifiedSlideHeight]);

  const heroSwiperOptions = useMemo(() => ({
    threshold: 8,
    touchStartPreventDefault: false,
    onSlideChange(swiper) {
      syncActiveSlideFromSwiper(swiper);
      scheduleMeasureUnifiedHeight({force: true});
    },
    onSwiper(swiper) {
      swiperRef.current = swiper;
      syncActiveSlideFromSwiper(swiper);
    },
  }), [scheduleMeasureUnifiedHeight, syncActiveSlideFromSwiper]);

  const handleArClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    withCylindoViewer(cylindoWrapperRef, (v) => {
      v.ar = true;
    });
  }, []);

  const handleZoomClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    withCylindoViewer(cylindoWrapperRef, (v) => {
      v.zoom = Math.min((Number(v.zoom) || 1) + 0.5, 3);
      setIsCylindoZoomedIn(true);
    });
  }, []);

  const handleZoomOutClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    withCylindoViewer(cylindoWrapperRef, (v) => {
      const next = Math.max((Number(v.zoom) || 1) - 0.5, 1);

      if (next <= CYLINDO_ZOOMED_IN_THRESHOLD) {
        v.zoomStop?.();
        setIsCylindoZoomedIn(false);
      } else {
        v.zoom = next;
      }
    });
  }, []);

  const onCylindoZoomChange = useCallback((level) => {
    setIsCylindoZoomedIn(level > CYLINDO_ZOOMED_IN_THRESHOLD);
  }, []);

  const handleOpenFullscreen = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsCylindoFullscreen(true);
  }, []);

  const handleCloseFullscreen = useCallback(() => {
    setIsCylindoFullscreen(false);
  }, []);

  const handleVideoMuteChange = useCallback((index, muted) => {
    setVideoMutedMap((prev) => ({...prev, [index]: muted}));
  }, []);

  const onCylindoError = useCallback(() => setCylindoError(true), []);

  // ─── Effects ──────────────────────────────────────────────────────────────
  // Sync the stale-product ref after DOM updates. useLayoutEffect fires before useEffect,
  // so the ref is up-to-date before useCylindoAvailability triggers its own re-render.
  useLayoutEffect(() => {
    galleryProductRef.current = galleryProduct;
  }, [galleryProduct]);

  // Reset to first slide when hero slide URLs change (variant switch).
  useEffect(() => {
    prevRealIndexForLoopMuteRef.current = 0;
    if (swiperRef.current) {
      swiperRef.current.slideTo(0);
      setActiveSlideIndex(0);
    }
  }, [heroMediaKey]);

  useLayoutEffect(() => {
    skipMobileRemeasureRef.current = false;
    setUnifiedSlideMinPx(null);
    scheduleMeasureUnifiedHeight({force: true});
  }, [heroMediaKey, scheduleMeasureUnifiedHeight]);

  useEffect(() => {
    const root = heroSwiperRef.current;
    if (!root) return;

    const ro = new ResizeObserver(() => scheduleMeasureUnifiedHeight());
    ro.observe(root);
    window.addEventListener('resize', scheduleMeasureUnifiedHeight);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', scheduleMeasureUnifiedHeight);
    };
  }, [heroMediaKey, scheduleMeasureUnifiedHeight]);

  useEffect(() => {
    if (unifiedSlideMinPx != null && swiperRef.current) {
      updateSwiperPreservingSlide(swiperRef.current);
    }
  }, [unifiedSlideMinPx]);

  // Cylindo's native fullscreen is trapped inside the Chakra Drawer stacking context.
  // We use a portal at document.body instead, locking scroll while it's open.
  useEffect(() => {
    if (!isCylindoFullscreen || typeof document === 'undefined') return;

    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    return () => {
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('touch-action');
    };
  }, [isCylindoFullscreen]);

  // Reset Cylindo error on product change so the new product gets a fresh availability check.
  useEffect(() => {
    setCylindoError(false);
    setIsCylindoZoomedIn(false);
  }, [galleryProduct]);

  // ─── Render ───────────────────────────────────────────────────────────────
  if (!product) {
    return <Skeleton />;
  }

  if (isLoading || isProductSwitching) {
    return <Skeleton size={size} />;
  }

  const loadingStrategy = lazy ? 'lazy' : 'eager';

  if (has3D && cylindoParams && !cylindoError) {
    return (
      <Box
        ref={cylindoWrapperRef}
        aria-label={`3D viewer for ${galleryProduct?.name || 'product'}`}
        role="region"
        {...styles.cylindoViewerWrapper}
      >
        {!hideOverlayButtons && (
          <>
            {!isCylindoZoomedIn && (
              <HStack aria-hidden="true" {...styles.cylindoOverlayLeft}>
                <Box {...styles.cylindoDragToRotateBadge}>
                  <DragToRotateIcon {...styles.cylindoDragToRotateIcon} />
                  <Text {...styles.cylindoDragToRotateText}>
                    <FormattedMessage
                      defaultMessage="Drag to rotate"
                      id="product_image_gallery.cylindo.drag_to_rotate"
                    />
                  </Text>
                </Box>
              </HStack>
            )}

            <HStack aria-label="3D viewer controls" role="toolbar" spacing={2} {...styles.cylindoOverlayRight}>
              {hasAR && (forceCylindoCheck || !isSimpleProduct) && imageSource === 'cylindo' && (
                <CylindoARLink
                  arqrUrl={cylindoParams.arqrUrl}
                  buttonText="See it in your space"
                  productName={galleryProduct?.name}
                  buttonProps={{
                    ...styles.cylindoArButton,
                    gap: 0,
                    leftIcon: <ArIcon />,
                    px: 1,
                  }}
                  tabIndex={0}
                  onMobileClick={handleArClick}
                />
              )}
              <IconButton
                aria-label="Zoom image"
                icon={<CylindoZoomIcon {...styles.cylindoZoomIcon} />}
                size="sm"
                variant="outline"
                {...styles.cylindoZoomButton}
                onClick={handleZoomClick}
                tabIndex={0}
              />
              <IconButton
                aria-label="Zoom out image"
                icon={<CylindoZoomOutIcon {...styles.cylindoZoomIcon} />}
                isDisabled={!isCylindoZoomedIn}
                size="sm"
                variant="outline"
                {...styles.cylindoZoomButton}
                onClick={handleZoomOutClick}
                tabIndex={0}
              />
            </HStack>

            <IconButton
              aria-label="Fullscreen view"
              icon={<FullscreenIcon {...styles.cylindoFullscreenIcon} />}
              size="sm"
              variant="outline"
              {...styles.cylindoFullscreenButton}
              onClick={handleOpenFullscreen}
              tabIndex={0}
            />
          </>
        )}

        <CylindoViewer
          code={cylindoParams.productCode}
          configuration={cylindoParams.configuration}
          customerId={cylindoParams.customerId}
          onError={onCylindoError}
          onZoomChange={onCylindoZoomChange}
        />

        {/* Fullscreen portal — rendered at document.body to escape all stacking contexts */}
        {isCylindoFullscreen && typeof document !== 'undefined' && createPortal(
          <Box
            bg="white"
            display="flex"
            flexDirection="column"
            position="fixed"
            zIndex={9999}
            {...(viewportRect ? {
              height: `${viewportRect.height}px`,
              left: `${viewportRect.left}px`,
              top: `${viewportRect.top}px`,
              width: `${viewportRect.width}px`,
            } : {
              bottom: 0,
              left: 0,
              right: 0,
              top: 0,
            })}
          >
            <IconButton
              aria-label="Close fullscreen"
              color="gray.900"
              position="absolute"
              right={3}
              size="md"
              top={3}
              variant="ghost"
              zIndex={1}
              _hover={{color: 'gray.900'}}
              _active={{color: 'gray.900'}}
              icon={<CloseIcon boxSize="20px" />}
              onClick={handleCloseFullscreen}
            />
            <Box alignItems="center" display="flex" flex={1} justifyContent="center">
              <CylindoViewer
                code={cylindoParams.productCode}
                configuration={cylindoParams.configuration}
                customerId={cylindoParams.customerId}
              />
            </Box>
          </Box>,
          document.body
        )}
      </Box>
    );
  }

  if (heroImageGroup?.images?.length > 0) {
    return (
      <Box
        ref={heroSwiperRef}
        aria-label={`Image gallery for ${galleryProduct?.name || 'product'}`}
        role="region"
        sx={{
          '--hero-gallery-slide-min-h': unifiedSlideMinPx != null ? `${unifiedSlideMinPx}px` : undefined,
          '--hero-gallery-slide-max-h': UNIFIED_SLIDE_MIN_H_MAX_DESKTOP_PX + 'px',
          ...(styles.heroSwiper?.sx || {}),
        }}
      >
        <Swiper
          className={isSimpleProduct ? 'product-gallery product-gallery-simple' : 'product-gallery product-gallery-complex'}
          options={heroSwiperOptions}
          presets={isSimpleProduct ? 'product_gallery' : 'product_gallery_complex'}
        >
          {heroImageGroup.images.map((media, index) => {
            if (media.mediaType === 'video') {
              return (
                <GalleryVideoSlide
                  key={`video-${media.videoUrl || index}`}
                  alt={media.alt || `Product video ${index + 1}`}
                  containerProps={styles.videoSlide}
                  isActive={activeSlideIndex === index}
                  isMuted={videoMutedMap[index] ?? true}
                  muteButtonProps={styles.videoMuteButton}
                  muteControlProps={styles.videoMuteControl}
                  playPauseButtonProps={styles.videoPlayPauseButton}
                  playPauseControlProps={styles.videoPlayPauseControl}
                  videoUrl={media.videoUrl}
                  onMutedChange={(muted) => handleVideoMuteChange(index, muted)}
                />
              );
            }

            return (
              <DynamicImage
                key={`image-${index}`}
                src={media.disBaseLink || media.link}
                widths={widths}
                imageProps={{
                  alt: media.alt || `Product image ${index + 1}`,
                  loading: index === 0 ? loadingStrategy : 'lazy',
                  title: media.title,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  onLoad: () => scheduleMeasureUnifiedHeight({force: true}),
                }}
              />
            );
          })}
        </Swiper>

        {/* Show AR link when Cylindo has AR (flyout: forceCylindoCheck; PDP: complex product only) */}
        {hasAR && cylindoParams && (forceCylindoCheck || !isSimpleProduct) && imageSource === 'cylindo' && (
          <CylindoARLink
            arqrUrl={cylindoParams.arqrUrl}
            productName={galleryProduct?.name}
          />
        )}
      </Box>
    );
  }

  return (
    <ImageNotAvailable
      wrapperProps={styles.placeholderWrapper}
      svgProps={styles.placeholderImage}
    />
  );
};

ImageGallery.propTypes = {
  /**
   * When true, allow Cylindo 3D/AR for any product with Cylindo attributes, without
   * requiring isSimpleProduct=false. Used in the customize flyout so option products
   * (e.g. cushions, legs) can show 3D when configured. Main PDP keeps default false.
   */
  forceCylindoCheck: PropTypes.bool,

  /**
   * When true, hide overlay buttons (Drag to rotate, See it in your space, Zoom, Fullscreen).
   * Used in mobile gallery panel for cleaner view.
   */
  hideOverlayButtons: PropTypes.bool,

  /**
   * Whether this is a simple product (finished good)
   */
  isSimpleProduct: PropTypes.bool,

  /**
   * Determines whether the image will be lazy loaded or not
   */
  lazy: PropTypes.bool,

  /**
   * Product object (needed for Cylindo components)
   */
  product: PropTypes.object,

  /**
   * The current selected variation values
   */
  selectedVariationAttributes: PropTypes.object,

  /**
   * Size of the Image gallery, this will be used to determined the max width from styles
   */
  size: PropTypes.string,
};

export default ImageGallery;
